// @ts-nocheck
/**
 * Custom Size Measurement Modal
 * ─────────────────────────────────────────────────────────────────────────────
 * Opens when the customer selects the "Custom" size variant in the picker.
 *
 * Each measurement field has TWO ways to input a value:
 *   • Type directly in the number input
 *   • Choose from the select dropdown (10 – 80 inches, 0.5-inch steps)
 * Both are kept in sync automatically.
 *
 * On "ADD TO CART" it injects measurements as Shopify line item properties
 * into the native product form, then programmatically triggers cart add.
 */

/* ── Field definitions ───────────────────────────────────────────────────── */

const FIELDS = [
  { name: 'bust',          label: 'Bust'              },
  { name: 'waist',         label: 'Waist'             },
  { name: 'hip',           label: 'Hip'               },
  { name: 'shoulder',      label: 'Shoulder'          },
  { name: 'height',        label: 'Height'            },
  { name: 'top_length',    label: 'Top / Dress Length'},
  { name: 'bottom_length', label: 'Bottom Length'     },
  { name: 'knee_length',   label: 'Knee Length'       },
  { name: 'sleeve',        label: 'Sleeve Length'     },
];

const CUSTOM_VALUE = 'custom';
const LS_PREFIX    = 'trovelle_cs_';

/* ── Modal DOM creation ───────────────────────────────────────────────────── */

/**
 * Creates the custom size modal DOM and appends it to body.
 * Called when __CS_CONFIG.hasCustom is true.
 */
function createModal() {
  const config = window.__CS_CONFIG;
  if (!config?.hasCustom || !config.productId) return null;

  const modalId = `cs-modal-${config.productId}`;

  const modal = document.createElement('div');
  modal.id = modalId;
  modal.className = 'cs-modal';
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', `${modalId}-title`);
  modal.setAttribute('hidden', '');
  modal.dataset.productId = config.productId;
  modal.dataset.customValueId = config.customValueId || '';

  const panel = document.createElement('div');
  panel.className = 'cs-modal__panel';
  panel.setAttribute('role', 'dialog');

  const header = document.createElement('div');
  header.className = 'cs-modal__header';
  const title = document.createElement('h2');
  title.className = 'cs-modal__title';
  title.id = `${modalId}-title`;
  title.textContent = 'Enter Your Measurements';
  header.appendChild(title);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'cs-modal__close';
  closeBtn.dataset.csClose = modalId;
  closeBtn.setAttribute('aria-label', 'Close measurements panel');
  closeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
  header.appendChild(closeBtn);

  panel.appendChild(header);

  const scrollArea = document.createElement('div');
  scrollArea.className = 'cs-modal__scroll';

  const note = document.createElement('p');
  note.className = 'cs-modal__note';
  note.innerHTML = 'All measurements in <strong>inches</strong>. Type a value or choose from the dropdown.';
  scrollArea.appendChild(note);

  const form = document.createElement('form');
  form.className = 'cs-modal__form';
  form.setAttribute('novalidate', '');

  const grid = document.createElement('div');
  grid.className = 'cs-modal__grid';

  const labels = [
    'BUST', 'WAIST', 'HIP', 'SHOULDER', 'HEIGHT',
    'DRESS / TOP LENGTH', 'BOTTOM LENGTH', 'KNEE LENGTH', 'SLEEVE LENGTH'
  ];

  FIELDS.forEach((field, i) => {
    const fieldEl = document.createElement('div');
    fieldEl.className = 'cs-modal__field';
    const label = document.createElement('label');
    label.className = 'cs-modal__label';
    label.htmlFor = `${modalId}-${field.name}`;
    label.textContent = labels[i];
    const group = document.createElement('div');
    group.className = 'cs-field__group';
    const input = document.createElement('input');
    input.type = 'number';
    input.id = `${modalId}-${field.name}`;
    input.name = field.name;
    input.className = 'cs-modal__input';
    input.placeholder = '—';
    input.min = '10';
    input.max = '80';
    input.step = '0.5';
    input.autocomplete = 'off';
    input.setAttribute('inputmode', 'decimal');
    const sep = document.createElement('span');
    sep.className = 'cs-field__sep';
    sep.setAttribute('aria-hidden', 'true');
    const select = document.createElement('select');
    select.className = 'cs-field__select';
    select.setAttribute('aria-label', `${labels[i]} preset`);
    select.tabIndex = -1;
    group.appendChild(input);
    group.appendChild(sep);
    group.appendChild(select);
    fieldEl.appendChild(label);
    fieldEl.appendChild(group);
    grid.appendChild(fieldEl);
  });

  form.appendChild(grid);

  const notesWrap = document.createElement('div');
  notesWrap.className = 'cs-modal__notes-wrap';
  const notesLabel = document.createElement('label');
  notesLabel.className = 'cs-modal__label';
  notesLabel.htmlFor = `${modalId}-notes`;
  notesLabel.textContent = 'ADDITIONAL NOTES (OPTIONAL)';
  const textarea = document.createElement('textarea');
  textarea.id = `${modalId}-notes`;
  textarea.name = 'notes';
  textarea.className = 'cs-modal__notes';
  textarea.placeholder = 'E.g. prefer a relaxed fit, longer sleeves, any other special requests…';
  textarea.rows = 3;
  notesWrap.appendChild(notesLabel);
  notesWrap.appendChild(textarea);
  form.appendChild(notesWrap);

  const status = document.createElement('div');
  status.className = 'cs-modal__status';
  status.setAttribute('aria-live', 'polite');
  status.setAttribute('hidden', '');
  form.appendChild(status);

  const disclaimer = document.createElement('p');
  disclaimer.className = 'cs-modal__disclaimer';
  disclaimer.textContent = 'Our team will reach out to confirm measurements after your order is placed.';
  form.appendChild(disclaimer);

  scrollArea.appendChild(form);
  panel.appendChild(scrollArea);

  const footer = document.createElement('div');
  footer.className = 'cs-modal__footer';
  const atcBtn = document.createElement('button');
  atcBtn.type = 'submit';
  atcBtn.className = 'cs-modal__atc';
  atcBtn.setAttribute('form', form.id || `cs-form-${config.productId}`);
  const atcText = document.createElement('span');
  atcText.className = 'cs-modal__atc-text';
  atcText.textContent = 'ADD TO CART';
  const spinner = document.createElement('span');
  spinner.className = 'cs-modal__atc-spinner';
  spinner.setAttribute('hidden', '');
  spinner.setAttribute('aria-hidden', 'true');
  atcBtn.appendChild(atcText);
  atcBtn.appendChild(spinner);
  footer.appendChild(atcBtn);
  panel.appendChild(footer);

  form.id = `cs-form-${config.productId}`;
  atcBtn.setAttribute('form', form.id);

  modal.appendChild(panel);
  document.body.appendChild(modal);

  return modal;
}

/* ── localStorage helpers ─────────────────────────────────────────────────── */

function lsSave(productId, data) {
  try { localStorage.setItem(`${LS_PREFIX}${productId}`, JSON.stringify(data)); } catch { /**/ }
}

function lsLoad(productId) {
  try { return JSON.parse(localStorage.getItem(`${LS_PREFIX}${productId}`) || 'null'); } catch { return null; }
}

function lsClear(productId) {
  try { localStorage.removeItem(`${LS_PREFIX}${productId}`); } catch { /**/ }
}

/* ── Select option builder ────────────────────────────────────────────────── */

/**
 * Populate a <select> with preset inch values from 10 to 80 in 0.5 steps.
 * Called once per field after the modal is in the DOM.
 */
function buildSelectOptions(select) {
  const frag = document.createDocumentFragment();

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = 'Select';
  frag.appendChild(placeholder);

  for (let v = 10; v <= 80; v = parseFloat((v + 0.5).toFixed(1))) {
    const opt = document.createElement('option');
    opt.value  = String(v);
    // Display: "36" or "36.5" – no trailing zeros for whole numbers
    opt.textContent = `${v % 1 === 0 ? v : v} in`;
    frag.appendChild(opt);
  }

  select.appendChild(frag);
}

/**
 * Wire bidirectional sync between a number <input> and its paired <select>.
 * • Choosing from dropdown → sets input value
 * • Typing in input       → tries to match a select option
 */
function wireInputSelect(input, select) {
  // Select → Input
  select.addEventListener('change', () => {
    if (select.value !== '') {
      input.value = select.value;
      // Dispatch 'input' so autoSave fires
      input.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      input.value = '';
    }
  });

  // Input → Select (best-effort match)
  input.addEventListener('input', () => {
    const v = parseFloat(input.value);
    if (!isNaN(v)) {
      const match = Array.from(select.options).find(
        o => o.value !== '' && Math.abs(parseFloat(o.value) - v) < 0.001
      );
      select.value = match ? match.value : '';
    } else {
      select.value = '';
    }
  });
}

/**
 * Build all select options and wire sync for every field in a given modal.
 */
function initFieldWidgets(modal) {
  modal.querySelectorAll('.cs-field__group').forEach(group => {
    const input  = group.querySelector('.cs-modal__input');
    const select = group.querySelector('.cs-field__select');
    if (!input || !select) return;

    // Only build once
    if (!select.dataset.built) {
      buildSelectOptions(select);
      select.dataset.built = '1';
    }

    wireInputSelect(input, select);
  });
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */

function buildMeasurements(form) {
  return FIELDS.map(({ name, label }) => {
    const val = form.querySelector(`[name="${name}"]`)?.value?.trim();
    return `${label}: ${val ? `${val} in` : 'N/A'}`;
  }).join(' | ');
}

function setHidden(form, name, value) {
  let el = form.querySelector(`[name="${CSS.escape(name)}"]`);
  if (!el) {
    el = Object.assign(document.createElement('input'), { type: 'hidden', name });
    form.appendChild(el);
  }
  el.value = value;
}

function removeHidden(form, name) {
  form.querySelector(`[name="${CSS.escape(name)}"]`)?.remove();
}

function getProductForm(productId) {
  const pfc = document.querySelector(`product-form-component[data-product-id="${productId}"]`);
  return pfc?.querySelector('form.product-form, form[action="/cart/add"]') ?? null;
}

function getAtcButton(productId) {
  const pfc = document.querySelector(`product-form-component[data-product-id="${productId}"]`);
  if (!pfc) return null;
  return (
    pfc.querySelector('[ref="addToCartButton"]') ||
    pfc.querySelector('add-to-cart-component button[type="submit"]') ||
    pfc.querySelector('button[type="submit"]')
  );
}

/* ── Modal open / close ───────────────────────────────────────────────────── */

function openModal(modal) {
  // Build select options on first open
  initFieldWidgets(modal);

  modal.removeAttribute('hidden');
  document.body.style.overflow = 'hidden';

  // Restore saved measurements
  const productId = modal.dataset.productId;
  const saved     = lsLoad(productId);
  if (saved) {
    const form = modal.querySelector('.cs-modal__form');
    FIELDS.forEach(({ name }) => {
      const input  = form.querySelector(`[name="${name}"]`);
      if (!input || !saved[name]) return;
      input.value = saved[name];
      // Sync select
      const select = input.closest('.cs-field__group')?.querySelector('.cs-field__select');
      if (select) select.value = saved[name];
    });
    const notes = form.querySelector('[name="notes"]');
    if (notes && saved.notes) notes.value = saved.notes;
  }

  setTimeout(() => modal.querySelector('.cs-modal__input')?.focus(), 80);
}

function closeModal(modal) {
  modal.setAttribute('hidden', '');
  document.body.style.overflow = '';

  // Remove injected line-item properties from the native form
  const productId = modal.dataset.productId;
  const form      = getProductForm(productId);
  if (form) {
    removeHidden(form, 'properties[Custom Measurements]');
    removeHidden(form, 'properties[Size]');
    removeHidden(form, 'properties[Additional Notes]');
  }
}

/* ── Status message ───────────────────────────────────────────────────────── */

function showStatus(modal, type, msg) {
  const el = modal.querySelector('.cs-modal__status');
  if (!el) return;
  el.className = `cs-modal__status cs-modal__status--${type}`;
  el.textContent = msg;
  el.removeAttribute('hidden');
}

function clearStatus(modal) {
  const el = modal.querySelector('.cs-modal__status');
  if (el) { el.setAttribute('hidden', ''); el.textContent = ''; el.className = 'cs-modal__status'; }
}

/* ── Auto-save ────────────────────────────────────────────────────────────── */

function autoSave(modal) {
  const productId = modal.dataset.productId;
  const form      = modal.querySelector('.cs-modal__form');
  const data      = {};

  FIELDS.forEach(({ name }) => {
    const v = form.querySelector(`[name="${name}"]`)?.value?.trim();
    if (v) data[name] = v;
  });
  const notes = form.querySelector('[name="notes"]')?.value?.trim();
  if (notes) data.notes = notes;

  lsSave(productId, data);
}

/* ── Modal form submit ────────────────────────────────────────────────────── */

async function handleModalSubmit(modal) {
  clearStatus(modal);

  const productId   = modal.dataset.productId;
  const form        = modal.querySelector('.cs-modal__form');
  const productForm = getProductForm(productId);
  const atcBtn      = getAtcButton(productId);

  if (!productForm) {
    showStatus(modal, 'error', 'Could not find the product form. Please refresh and try again.');
    return;
  }

  const submitBtn  = modal.querySelector('.cs-modal__atc');
  const submitText = modal.querySelector('.cs-modal__atc-text');
  const spinner    = modal.querySelector('.cs-modal__atc-spinner');

  const measurements = buildMeasurements(form);
  const notes        = form.querySelector('[name="notes"]')?.value?.trim() || '';

  setHidden(productForm, 'properties[Custom Measurements]', measurements);
  setHidden(productForm, 'properties[Size]', 'Custom');
  if (notes) setHidden(productForm, 'properties[Additional Notes]', notes);

  submitBtn.disabled      = true;
  submitText.textContent  = 'Adding…';
  spinner.removeAttribute('hidden');

  await new Promise(r => setTimeout(r, 40));

  try {
    if (atcBtn && !atcBtn.disabled) {
      atcBtn.click();
    } else {
      productForm.requestSubmit?.() ?? productForm.submit?.();
    }

    await new Promise(r => setTimeout(r, 600));
    closeModal(modal);
    lsClear(productId);

  } catch (err) {
    console.error('[CustomSize] Add to cart error:', err);
    showStatus(modal, 'error', 'Could not add to cart. Please try again.');
  } finally {
    submitBtn.disabled     = false;
    submitText.textContent = 'ADD TO CART';
    spinner.setAttribute('hidden', '');
  }
}

/* ── Variant detection ────────────────────────────────────────────────────── */

/**
 * Returns true if any variant-picker on the page currently has "Custom" selected.
 */
function isCustomSelected() {
  // Radio-button style picker
  const checkedRadio = document.querySelector(
    'variant-picker input[type="radio"][value="Custom"]:checked, ' +
    'variant-picker input[type="radio"][value="custom"]:checked, ' +
    '.variant-option input[type="radio"][value="Custom"]:checked'
  );
  if (checkedRadio) return true;

  // Select/dropdown style picker
  const activeSelect = document.querySelector('variant-picker select');
  if (activeSelect) {
    const v = activeSelect.value.toLowerCase();
    if (v === CUSTOM_VALUE) return true;
  }

  return false;
}

/**
 * Open or close the modal depending on whether "Custom" is currently selected.
 */
function syncModal() {
  const modal = document.querySelector('.cs-modal');
  if (!modal) return;

  const custom = isCustomSelected();
  console.log('[CustomSize] syncModal — isCustomSelected:', custom);

  if (custom) {
    if (modal.hasAttribute('hidden')) openModal(modal);
  } else {
    if (!modal.hasAttribute('hidden')) closeModal(modal);
  }
}

/* ── Main initialiser ─────────────────────────────────────────────────────── */

function init() {
  console.log('[CustomSize] init');

  /* 0. Create modal DOM from config (no HTML in Liquid) */
  if (window.__CS_CONFIG?.hasCustom) {
    createModal();
  }

  /* 1. Primary trigger: theme's variant:selected event */
  document.addEventListener('variant:selected', e => {
    console.log('[CustomSize] variant:selected fired', e.detail);
    syncModal();
  });

  /* 2. Fallback: capture-phase change event (dropdown pickers / timing edge cases) */
  document.addEventListener('change', e => {
    const inPicker = (
      e.target.closest?.('variant-picker input[type="radio"]') ||
      e.target.closest?.('.variant-option input[type="radio"]') ||
      e.target.closest?.('variant-picker select')
    );
    if (inPicker) {
      console.log('[CustomSize] change event in picker');
      // Small tick lets the picker update its checked state before we query it
      setTimeout(syncModal, 0);
    }
  }, true);

  /* 3. Belt-and-suspenders: click on variant label / button */
  document.addEventListener('click', e => {
    const label = e.target.closest?.('variant-picker label, .variant-option label');
    if (label) setTimeout(syncModal, 50);
  });

  /* 4. Close button (data-cs-close) */
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-cs-close]');
    if (!btn) return;
    const modal = document.getElementById(btn.dataset.csClose);
    if (!modal) return;

    closeModal(modal);

    // Revert picker to first non-Custom option
    const section    = modal.closest?.('.shopify-section, [data-section-id]');
    const firstOther = (section || document).querySelector(
      'variant-picker input[type="radio"]:not([value="Custom"]):not([value="custom"])'
    );
    if (firstOther) {
      firstOther.checked = true;
      firstOther.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* 5. Click on dark overlay (outside panel) closes modal */
  document.addEventListener('click', e => {
    if (e.target.classList?.contains('cs-modal')) closeModal(e.target);
  });

  /* 6. Escape key */
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.cs-modal:not([hidden])').forEach(m => closeModal(m));
  });

  /* 7. Auto-save on input change */
  document.addEventListener('input', e => {
    if (!e.target.closest?.('.cs-modal__input, .cs-modal__notes')) return;
    const modal = e.target.closest?.('.cs-modal');
    if (modal) autoSave(modal);
  });

  /* 8. Modal form submit (ADD TO CART button) */
  document.addEventListener('submit', e => {
    const form = e.target.closest?.('.cs-modal__form');
    if (!form) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const modal = form.closest('.cs-modal');
    if (modal) handleModalSubmit(modal);
  });

  /* 9. cart:update — close any open custom-size modal after successful cart add */
  document.addEventListener('cart:update', () => {
    document.querySelectorAll('.cs-modal:not([hidden])').forEach(modal => {
      closeModal(modal);
      lsClear(modal.dataset.productId);
    });
  });

  /* 10. Page load — open modal if Custom is already selected (e.g. via URL ?variant=…) */
  setTimeout(() => {
    if (isCustomSelected()) {
      console.log('[CustomSize] Custom already selected on page load — opening modal');
      syncModal();
    }
  }, 200);
}

/* ── Boot ─────────────────────────────────────────────────────────────────── */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
